import type { EventDef, Locked } from "./types";

export function buildIcs(
  ev: EventDef,
  locked: Locked,
  opts: { where?: string; url?: string; host: string },
): string {
  const uid = `overlap-${ev.id}-${locked.slotStart}@${opts.host}`;
  const stampMs = Date.parse(locked.lockedAt);
  const stamp = utcStamp(Number.isFinite(stampMs) ? Math.floor(stampMs / 60_000) : locked.slotStart);
  const start = utcStamp(locked.slotStart);
  const end = utcStamp(locked.slotStart + locked.durationMin);
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Overlap//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${escapeIcs(ev.title)}`,
  ];
  if (ev.note) lines.push(`DESCRIPTION:${escapeIcs(ev.note)}`);
  if (opts.where) lines.push(`LOCATION:${escapeIcs(opts.where)}`);
  if (opts.url) lines.push(`URL:${escapeIcs(opts.url)}`);
  lines.push("END:VEVENT", "END:VCALENDAR");
  return lines.map(foldLine).join("\r\n") + "\r\n";
}

export function googleCalendarUrl(ev: EventDef, locked: Locked, where?: string): string {
  const start = utcStamp(locked.slotStart);
  const end = utcStamp(locked.slotStart + locked.durationMin);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: ev.title,
    dates: `${start}/${end}`,
  });
  if (ev.note) params.set("details", ev.note);
  if (where) params.set("location", where);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function utcStamp(epochMin: number): string {
  const d = new Date(epochMin * 60_000);
  const p = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}` +
    `T${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}Z`
  );
}

function escapeIcs(text: string): string {
  return text
    .replace(/\\/g, "\\\\")
    .replace(/\r\n/g, "\n")
    .replace(/\n/g, "\\n")
    .replace(/\r/g, "")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

function foldLine(line: string): string {
  const encoder = new TextEncoder();
  const decoder = new TextDecoder();
  const bytes = encoder.encode(line);
  if (bytes.length <= 75) return line;
  const parts: string[] = [];
  let i = 0;
  let first = true;
  while (i < bytes.length) {
    const budget = first ? 75 : 74;
    let take = Math.min(budget, bytes.length - i);
    while (take > 0 && i + take < bytes.length && (bytes[i + take]! & 0b1100_0000) === 0b1000_0000) {
      take -= 1;
    }
    if (take < 1) take = 1;
    const chunk = decoder.decode(bytes.slice(i, i + take));
    parts.push(first ? chunk : ` ${chunk}`);
    first = false;
    i += take;
  }
  return parts.join("\r\n");
}
